import React, { useState, useEffect } from 'react'
import NavBar from '../../../component/b2c/navBar/navBar'
import Sidebar from '../../../component/b2c/sidebar/sidebar'
import {
  LayoutDashboard,
  Warehouse,
  Truck,
  CreditCard,
  Users,
  FileText,
  Settings as SettingsIcon,
  Check,
  Shield,
  Lock,
  Save,
  CheckCircle2,
  UserCog,
  Store,
  User as UserIcon,
  Briefcase,
  Plug,
  AlertCircle,
  Tag,
  Milestone,
  BadgePercent,
  Brain,
  HelpCircle,
  CircleUser,
} from 'lucide-react'
import EmployeeForm from './EmployeeForm'

const ROLES = [
  { key: 'Admin', label: 'Admin', icon: Shield, locked: true },
  { key: 'Manager', label: 'Manager', icon: UserCog, locked: false },
  { key: 'Accounts', label: 'Accounts', icon: Store, locked: false },
  { key: 'Staff', label: 'Customer Support', icon: Users, locked: false },
  { key: 'User', label: 'BDE', icon: Users, locked: false },
  { key: 'HR', label: 'HR', icon: UserIcon, locked: false },

]

const MODULES = [
  { key: 'Dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'HRMS', label: 'HRMS (ERP)', icon: Briefcase },
  { key: 'Shipments', label: 'Shipments', icon: Truck },
  { key: 'Warehouse', label: 'Warehouse', icon: Warehouse },
  { key: 'Integration', label: 'Integration', icon: Plug },
  { key: 'Disputes', label: 'Disputes', icon: AlertCircle },
  { key: 'Payments', label: 'Payments', icon: CreditCard },
  { key: 'Label', label: 'Label', icon: Tag },
  { key: 'Services', label: 'Services', icon: Milestone },
  { key: 'Offers', label: 'Offers', icon: BadgePercent },
  { key: 'Customers', label: 'Customers', icon: Users },
  { key: 'Preship', label: 'Preship', icon: Brain },
  { key: 'Reports', label: 'Reports', icon: FileText },
  { key: 'Help', label: 'Help', icon: HelpCircle },
  { key: 'Setting', label: 'Setting', icon: SettingsIcon },
  { key: 'Profile', label: 'Profile', icon: CircleUser },
]

const ACTIONS = [
  { key: 'view', label: 'View' },
  { key: 'add', label: 'Add' },
  { key: 'edit', label: 'Edit' },
  { key: 'delete', label: 'Delete' },
]

const EMPTY_ACTIONS = { view: false, add: false, edit: false, delete: false }

// default state generator
const buildDefaultPermissions = () => {
  const obj = {}
  ROLES.forEach((role) => {
    obj[role.key] = {}
    MODULES.forEach((mod) => {
      obj[role.key][mod.key] = {
        view:
          role.key === 'Admin' || role.key === 'Retailer'
            ? true
            : role.key === 'User'
              ? mod.key === 'Dashboard' || mod.key === 'Profile'
              : true,
        add: role.key === 'Admin',
        edit: role.key === 'Admin',
        delete: role.key === 'Admin',
      }
    })
  })
  return obj
}

const PermissionManagement = () => {
  // 👇 lazy init — function sirf ek baar chalega, har render pe nahi
  const [permissions, setPermissions] = useState(() => buildDefaultPermissions())
  const [activeRole, setActiveRole] = useState('Admin')
  const [toast, setToast] = useState(false)
  const [expandedMobile, setExpandedMobile] = useState(null)

  useEffect(() => {
    setPermissions((prev) => {
      let changed = false
      const next = { ...prev }
      ROLES.forEach((role) => {
        if (!next[role.key]) {
          next[role.key] = {}
          changed = true
        }
        MODULES.forEach((mod) => {
          if (!next[role.key][mod.key]) {
            next[role.key] = {
              ...next[role.key],
              [mod.key]: {
                view: role.key === 'Admin',
                add: role.key === 'Admin',
                edit: role.key === 'Admin',
                delete: role.key === 'Admin',
              },
            }
            changed = true
          }
        })
      })
      return changed ? next : prev
    })
  }, [])

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(false), 2500)
      return () => clearTimeout(t)
    }
  }, [toast])

  const isRoleLocked = ROLES.find((r) => r.key === activeRole)?.locked


  const getPerm = (role, modKey, actionKey) =>
    permissions?.[role]?.[modKey]?.[actionKey] ?? false

  const getModuleActions = (role, modKey) => permissions?.[role]?.[modKey] ?? EMPTY_ACTIONS

  const togglePermission = (role, modKey, actionKey) => {
    if (isRoleLocked) return
    setPermissions((prev) => ({
      ...prev,
      [role]: {
        ...prev[role],
        [modKey]: {
          ...EMPTY_ACTIONS,
          ...prev[role]?.[modKey],
          [actionKey]: !getPerm(role, modKey, actionKey),
        },
      },
    }))
  }

  const toggleRowAll = (role, modKey) => {
    if (isRoleLocked) return
    const current = getModuleActions(role, modKey)
    const allOn = ACTIONS.every((a) => current[a.key])
    setPermissions((prev) => ({
      ...prev,
      [role]: {
        ...prev[role],
        [modKey]: ACTIONS.reduce((acc, a) => ({ ...acc, [a.key]: !allOn }), {}),
      },
    }))
  }

  const toggleColumnAll = (role, actionKey) => {
    if (isRoleLocked) return
    const allOn = MODULES.every((m) => getPerm(role, m.key, actionKey))
    setPermissions((prev) => {
      const updated = { ...prev[role] }
      MODULES.forEach((m) => {
        updated[m.key] = { ...EMPTY_ACTIONS, ...updated[m.key], [actionKey]: !allOn }
      })
      return { ...prev, [role]: updated }
    })
  }

  // 👇 NEW: check if ALL modules+actions are ON for the active role
  const isAllSelected = (role) =>
    MODULES.every((m) => ACTIONS.every((a) => getPerm(role, m.key, a.key)))

  // 👇 NEW: toggle every checkbox for the active role at once
  const toggleSelectAll = (role) => {
    if (ROLES.find((r) => r.key === role)?.locked) return
    const turnOn = !isAllSelected(role)
    setPermissions((prev) => {
      const updated = {}
      MODULES.forEach((m) => {
        updated[m.key] = { view: turnOn, add: turnOn, edit: turnOn, delete: turnOn }
      })
      return { ...prev, [role]: updated }
    })
  }

  const fullAccessCount = (roleKey) =>
    MODULES.filter((m) => ACTIONS.every((a) => getPerm(roleKey, m.key, a.key))).length

  const handleSave = () => {
    // 🔌 API hook here:
    // await axios.post('/api/permissions', { permissions })
    console.log('Saving permissions:', permissions)
    setToast(true)
  }

  return (
    <div className="h-screen flex flex-col  overflow-hidden">
      <NavBar />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />

        <main className="flex-1 overflow-y-auto bg-gray-200 p-4 md:p-6">
          {/* Header */}
          <EmployeeForm />
          <div className="  px-6 bg-white">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Permission Management</h1>
            <p className="text-gray-500 mt-1 text-sm md:text-base">
              Control who can view, add, edit, or delete — based on roles.            </p>
          </div>
          {/* 
     <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
  <div className="rounded-2xl bg-gradient-to-br from-blue-400 to-blue-600 p-4 shadow-sm border border-gray-100">
    <p className="text-xs text-white/80 font-medium">Total Roles</p>
    <p className="text-2xl font-bold text-white mt-1">{ROLES.length}</p>
  </div>
  <div className="rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 p-4 shadow-sm border border-gray-100">
    <p className="text-xs text-white/80 font-medium">Total Modules</p>
    <p className="text-2xl font-bold text-white mt-1">{MODULES.length}</p>
  </div>
  <div className="rounded-2xl bg-gradient-to-br from-orange-400 to-orange-600 p-4 shadow-sm border border-gray-100">
    <p className="text-xs text-white/80 font-medium">Full Access ({activeRole})</p>
    <p className="text-2xl font-bold text-white mt-1">
      {fullAccessCount(activeRole)}/{MODULES.length}
    </p>
  </div>
  <div className="rounded-2xl bg-gradient-to-br from-purple-400 to-purple-600 p-4 shadow-sm border border-gray-100">
    <p className="text-xs text-white/80 font-medium">Status</p>
    <p className="text-2xl font-bold text-white mt-1">Active</p>
  </div>
</div> */}

          {/* Role Tabs + Select All */}
          <div className="flex items-center bg-white px-6 pt-4 pb-4 justify-between gap-3 ">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {ROLES.map((role) => {
                const Icon = role.icon
                const active = activeRole === role.key
                return (
                  <button
                    key={role.key}
                    onClick={() => setActiveRole(role.key)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm whitespace-nowrap transition-all duration-300 ${active
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-orange-500/30 scale-105'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                      }`}
                  >
                    <Icon className="w-4 h-4" />
                    {role.label}
                    {role.locked && <Lock className="w-3 h-3 opacity-70" />}
                  </button>
                )
              })}
            </div>

            {/* 👇 NEW: Select All checkbox - right side */}
            <label
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border whitespace-nowrap select-none transition-all duration-300 ${isRoleLocked
                  ? 'bg-gray-50 border-gray-100 cursor-not-allowed opacity-60'
                  : 'bg-white border-gray-200 cursor-pointer hover:border-blue-300'
                }`}
            >
              <button
                type="button"
                onClick={() => toggleSelectAll(activeRole)}
                disabled={isRoleLocked}
                className={`w-5 h-5 rounded-md flex items-center justify-center transition-all duration-200 ${isRoleLocked ? 'cursor-not-allowed' : 'hover:scale-110'
                  } ${isAllSelected(activeRole)
                    ? 'bg-gradient-to-br from-blue-500 to-blue-600'
                    : 'bg-gray-200'
                  }`}
              >
                <Check
                  className={`w-3.5 h-3.5 text-white transition-all duration-200 ${isAllSelected(activeRole) ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
                    }`}
                />
              </button>
              <span className="text-sm font-medium text-gray-600">{isAllSelected(activeRole) ? "Select None" : "Select All"}</span>
            </label>
          </div>

          {/* Permission Card */}
          <div className="bg-white rounded-b-2xl p-6 shadow-sm border border-gray-100 overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full rounded-lg ">
                <thead >
                  <tr className=" rounded-lg  border-white border-l-4 border-r-4 bg-gray-600">
                    <th className="text-left px-5 py-3 text-xs font-bold rolg text-white uppercase tracking-wide">
                      Module
                    </th>
                    {ACTIONS.map((action) => (
                      <th key={action.key} className="px-3 font-bold py-3">
                        <button
                          onClick={() => toggleColumnAll(activeRole, action.key)}
                          disabled={isRoleLocked}
                          className="text-xs font-bold text-white uppercase tracking-wide hover:text-orange-500 transition-colors disabled:cursor-not-allowed"
                        >
                          {action.label}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MODULES.map((mod, idx) => {
                    const Icon = mod.icon
                    return (
                      <tr
                        key={mod.key}
                        className={`border-b border-gray-50 hover:bg-orange-50/30 transition-colors duration-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                          }`}
                      >
                        <td className="px-5 py-3">
                          <button
                            onClick={() => toggleRowAll(activeRole, mod.key)}
                            disabled={isRoleLocked}
                            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors disabled:cursor-not-allowed"
                          >
                            <Icon className="w-4 h-4 text-gray-400" />
                            {mod.label}
                          </button>
                        </td>
                        {ACTIONS.map((action) => {
                          const checked = getPerm(activeRole, mod.key, action.key)
                          return (
                            <td key={action.key} className="px-3 py-3 text-center">
                              <button
                                onClick={() => togglePermission(activeRole, mod.key, action.key)}
                                disabled={isRoleLocked}
                                className={`w-6 h-6 rounded-md flex items-center justify-center mx-auto transition-all duration-200 ${isRoleLocked ? 'cursor-not-allowed opacity-70' : 'hover:scale-110'
                                  } ${checked
                                    ? 'bg-gradient-to-br from-blue-500 to-blue-600'
                                    : 'bg-gray-200 hover:bg-gray-300'
                                  }`}
                              >
                                <Check
                                  className={`w-4 h-4 text-white transition-all duration-200 ${checked ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
                                    }`}
                                />
                              </button>
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card List */}
            <div className="md:hidden divide-y divide-gray-50">
              {MODULES.map((mod) => {
                const Icon = mod.icon
                const isOpen = expandedMobile === mod.key
                return (
                  <div key={mod.key}>
                    <button
                      onClick={() => setExpandedMobile(isOpen ? null : mod.key)}
                      className="w-full flex items-center justify-between px-4 py-3.5"
                    >
                      <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <Icon className="w-4 h-4 text-gray-400" />
                        {mod.label}
                      </span>
                      <span className="text-xs text-gray-400">
                        {ACTIONS.filter((a) => getPerm(activeRole, mod.key, a.key)).length}/{ACTIONS.length}
                      </span>
                    </button>
                    <div
                      className={`grid grid-cols-2 gap-2 px-4 transition-all duration-300 overflow-hidden ${isOpen ? 'max-h-40 pb-4 opacity-100' : 'max-h-0 opacity-0'
                        }`}
                    >
                      {ACTIONS.map((action) => {
                        const checked = getPerm(activeRole, mod.key, action.key)
                        return (
                          <button
                            key={action.key}
                            onClick={() => togglePermission(activeRole, mod.key, action.key)}
                            disabled={isRoleLocked}
                            className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${checked
                              ? 'bg-orange-500 text-white'
                              : 'bg-gray-100 text-gray-500'
                              }`}
                          >
                            {action.label}
                            <Check className={`w-3.5 h-3.5 transition-opacity ${checked ? 'opacity-100' : 'opacity-0'}`} />
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="flex justify-end mt-5">
              <button
                onClick={handleSave}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-2.5 rounded-xl font-medium shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:scale-105 transition-all duration-300"
              >
                <Save className="w-4 h-4" />
                Save Changes
              </button>
            </div>
          </div>

          {/* Save Button */}

        </main>
      </div>

      {/* Toast */}
      <div
        className={`fixed bottom-6 right-6 flex items-center gap-2 bg-gray-900 text-white px-4 py-3 rounded-xl shadow-2xl transition-all duration-300 ${toast ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0 pointer-events-none'
          }`}
      >
        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
        Permissions updated successfully
      </div>
    </div>
  )
}

export default PermissionManagement